import { useState } from 'preact/hooks';
import type { MessageBlock } from './types';

/**
 * Rich parts under an assistant message: quick-reply buttons, free appointment times, and a
 * carousel of cards. Tapping sends the choice as the visitor's own message. Buttons and times
 * only make sense as the latest answer, so they show only while `active`; cards stay (their
 * links are still useful later).
 */
export function MessageBlocks({
  blocks,
  active,
  onPick,
  onDetails,
}: {
  blocks: MessageBlock[];
  active: boolean;
  onPick(text: string): void;
  /** Opens the details form. */
  onDetails?(block: Extract<MessageBlock, { type: 'details_form' }>): void;
}) {
  return (
    <>
      {blocks.map((b, i) => {
        if (b.type === 'buttons') {
          return active ? (
            <div key={i} class="vs-quick" role="group" aria-label="Quick replies">
              {b.options.map((o) => (
                <button key={o} type="button" class="vs-chip" onClick={() => onPick(o)}>
                  {o}
                </button>
              ))}
            </div>
          ) : null;
        }
        if (b.type === 'slots') {
          return active ? <Slots key={i} block={b} onPick={onPick} /> : null;
        }
        if (b.type === 'meeting') return <Meeting key={i} block={b} />;
        if (b.type === 'details_form') {
          return active ? (
            <button key={i} type="button" class="vs-details-open" onClick={() => onDetails?.(b)}>
              📝 {b.title}: tap to fill in
            </button>
          ) : null;
        }
        return <Cards key={i} block={b} active={active} onPick={onPick} />;
      })}
    </>
  );
}

/** A booked meeting stays visible: what, when, and the link to join or copy. */
function Meeting({ block }: { block: Extract<MessageBlock, { type: 'meeting' }> }) {
  const [copied, setCopied] = useState(false);
  async function copy() {
    if (!block.url) return;
    try {
      await navigator.clipboard.writeText(block.url);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      window.prompt('Copy the meeting link:', block.url);
    }
  }
  return (
    <div class="vs-meeting" role="group" aria-label="Booked meeting">
      <div class="vs-meeting-congrats">🎉 Your meeting is booked!</div>
      <div class="vs-meeting-title">{block.title}</div>
      <div class="vs-meeting-when">{block.when}</div>
      {block.url ? (
        <div class="vs-meeting-actions">
          <a class="vs-meeting-join" href={block.url} target="_blank" rel="noopener noreferrer">
            Join meeting
          </a>
          <button type="button" class="vs-meeting-copy" onClick={() => void copy()}>
            {copied ? 'Copied ✓' : 'Copy link'}
          </button>
        </div>
      ) : (
        <div class="vs-meeting-when">
          The invite with all the details is on its way to your email.
        </div>
      )}
    </div>
  );
}

type Details = { name: string; phone: string; email: string };

/** The details form, opened over the chat. Every field is typed, so nothing is misspelt. */
export function DetailsSheet({
  block,
  onSubmit,
  onClose,
  inline = false,
  optInLabel = null,
  callOptInLabel = null,
}: {
  block: Extract<MessageBlock, { type: 'details_form' }>;
  onSubmit(details: Details, whatsappOptIn: boolean, callOptIn: boolean): void;
  onClose(): void;
  /** Inside the call screen instead of over the chat. */
  inline?: boolean;
  /** The WhatsApp opt-in checkbox's text, or null for none. */
  optInLabel?: string | null;
  /** The calls consent checkbox's text, or null for none. */
  callOptInLabel?: string | null;
}) {
  const [optIn, setOptIn] = useState(false);
  const [callOptIn, setCallOptIn] = useState(false);
  const [values, setValues] = useState<Details>(block.prefill);
  const [errors, setErrors] = useState<Partial<Record<keyof Details, string>>>({});
  const set = (k: keyof Details) => (e: Event) =>
    setValues((v) => ({ ...v, [k]: (e.target as HTMLInputElement).value }));

  function submit(e: Event) {
    e.preventDefault();
    const found: Partial<Record<keyof Details, string>> = {};
    if (!values.name.trim()) found.name = 'Please enter your name';
    if (values.phone.replace(/\D/g, '').length < 7 || /[^\d\s+()-]/.test(values.phone)) {
      found.phone = 'Please enter a valid phone number';
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(values.email.trim())) {
      found.email = 'Please enter a valid email';
    }
    setErrors(found);
    if (Object.keys(found).length) return;
    onSubmit(
      {
        name: values.name.trim(),
        phone: values.phone.trim(),
        email: values.email.trim().toLowerCase(),
      },
      optIn,
      callOptIn,
    );
  }

  const fields: [keyof Details, string, string, string][] = [
    ['name', 'Your name', 'text', 'name'],
    ['phone', 'Phone number', 'tel', 'tel'],
    ['email', 'Email (the invite is sent here)', 'email', 'email'],
  ];
  const form = (
    <form
      class={inline ? 'vs-voice-form vs-prechat' : 'vs-sheet vs-prechat'}
      role="dialog"
      aria-label={block.title}
      onSubmit={submit}
      noValidate
    >
      <div class="vs-sheet-head">
        <div class="vs-prechat-title">{block.title}</div>
        <button type="button" class="vs-sheet-close" aria-label="Close" onClick={onClose}>
          ✕
        </button>
      </div>
      {fields.map(([k, label, type, auto]) => (
        <div key={k} class="vs-field">
          <label for={`vs-df-${k}`}>{label}</label>
          <input
            id={`vs-df-${k}`}
            type={type}
            autocomplete={auto}
            value={values[k]}
            aria-invalid={errors[k] ? true : undefined}
            onInput={set(k)}
          />
          {errors[k] && <div class="vs-field-error">{errors[k]}</div>}
        </div>
      ))}
      {optInLabel && (
        <label class="vs-optin">
          <input
            type="checkbox"
            checked={optIn}
            onChange={(e) => setOptIn((e.target as HTMLInputElement).checked)}
          />
          <span>{optInLabel}</span>
        </label>
      )}
      {callOptInLabel && (
        <label class="vs-optin">
          <input
            type="checkbox"
            checked={callOptIn}
            onChange={(e) => setCallOptIn((e.target as HTMLInputElement).checked)}
          />
          <span>{callOptInLabel}</span>
        </label>
      )}
      <button type="submit" class="vs-prechat-submit">
        Confirm details
      </button>
    </form>
  );
  if (inline) return form;
  return (
    <div class="vs-sheet-backdrop" onClick={(e) => e.target === e.currentTarget && onClose()}>
      {form}
    </div>
  );
}

function Slots({
  block,
  onPick,
}: {
  block: Extract<MessageBlock, { type: 'slots' }>;
  onPick(text: string): void;
}) {
  const [dayIndex, setDayIndex] = useState(0);
  const day = block.days[dayIndex] ?? block.days[0];
  if (!day) return null;
  return (
    <div class="vs-slots" role="group" aria-label="Available times">
      {block.days.length > 1 && (
        <div class="vs-slot-days" role="tablist">
          {block.days.map((d, i) => (
            <button
              key={d.date}
              type="button"
              role="tab"
              aria-selected={i === dayIndex}
              class={`vs-slot-day${i === dayIndex ? ' is-on' : ''}`}
              onClick={() => setDayIndex(i)}
            >
              {d.label}
            </button>
          ))}
        </div>
      )}
      {block.days.length === 1 && <div class="vs-slot-title">{day.label}</div>}
      <div class="vs-slot-times">
        {day.times.map((t) => (
          <button
            key={t.time}
            type="button"
            class="vs-slot"
            onClick={() => onPick(`Book ${day.label} at ${t.label}`)}
          >
            {t.label}
          </button>
        ))}
      </div>
    </div>
  );
}

function Cards({
  block,
  active,
  onPick,
}: {
  block: Extract<MessageBlock, { type: 'cards' }>;
  active: boolean;
  onPick(text: string): void;
}) {
  return (
    <div class="vs-rcards" role="list">
      {block.items.map((c) => (
        <div key={c.title} class="vs-rcard" role="listitem">
          <div class="vs-rcard-title">{c.title}</div>
          {c.subtitle && <div class="vs-rcard-sub">{c.subtitle}</div>}
          <div class="vs-rcard-actions">
            {active && (
              <button
                type="button"
                class="vs-rcard-btn"
                onClick={() => onPick(`Tell me more about ${c.title}`)}
              >
                Choose
              </button>
            )}
            {c.url && (
              <a class="vs-rcard-link" href={c.url} target="_blank" rel="noopener noreferrer">
                View
              </a>
            )}
          </div>
        </div>
      ))}
    </div>
  );
}
